import React, { memo, useMemo } from 'react';
import { Cell, Row, TreeSelect } from '@dexma/ui-components';
import type { TreeSelectProps } from 'antd';
import { StyledSelectCell, StyledWidgetSelector } from 'adapter/in/common/components/Selector/style';
import { TreeSelectorDropdownStyles } from 'adapter/in/common/components/TreeSelector/style';
import { FooterInputText } from 'adapter/in/common/components/Text';
import { ENTITY_TYPE } from 'adapter/in/common/config/entityTypes';
import { SelectorOption, SelectorValueOptions, TreeSelectorOption } from 'adapter/in/common/form/types';

type TreeLabelValue = {
    label: React.ReactNode;
    value: string;
};

type TreeSelectValue = string | number | TreeLabelValue | Array<string | number | TreeLabelValue> | null | undefined;

export type FormTreeSelectorProps = {
    label: string;
    name: string;
    tree: TreeSelectorOption[];
    value: SelectorValueOptions | undefined;
    isLoading: boolean;
    error: boolean;
    onChange: (values: string[]) => void;
    placeholder?: string;
    maxSelectionLength: number;
    minSelectionLength: number;
    style?: React.CSSProperties;
    disabled?: boolean;
    errorMessage?: string;
    footer?: string;
};

const getOptionLabel = (option: SelectorOption): string => option.title ?? option.label ?? option.value;

const formatSelectedLabel = (topParentLabel: string, currentLabel: string): string =>
    topParentLabel === currentLabel ? currentLabel : `${topParentLabel} - ${currentLabel}`;

const getSelectedValuesByKeys = (nodes: TreeSelectorOption[], keys: string[]): TreeLabelValue[] => {
    const keySet = new Set(keys);
    const result: TreeLabelValue[] = [];

    const traverse = (options: TreeSelectorOption[], topParentLabel?: string) => {
        options.forEach((option) => {
            const optionLabel = getOptionLabel(option);
            const resolvedTopParentLabel = topParentLabel ?? optionLabel;

            if (keySet.has(option.value)) {
                result.push({ label: formatSelectedLabel(resolvedTopParentLabel, optionLabel), value: option.value });
            }
            if (option.children) {
                traverse(option.children, resolvedTopParentLabel);
            }
        });
    };

    traverse(nodes);

    keys.forEach((key) => {
        if (!result.some((selectedValue) => selectedValue.value === key)) {
            result.push({ label: key, value: key });
        }
    });

    return result;
};

const getSelectedKeys = (values?: SelectorValueOptions): string[] =>
    values ? values.map((value: SelectorOption) => value.value).filter(Boolean) : [];

const isTreeLabelValue = (value: unknown): value is TreeLabelValue => typeof value === 'object' && value !== null && 'value' in value;

const asStringValue = (value: string | number | TreeLabelValue): string => (isTreeLabelValue(value) ? String(value.value) : String(value));

const mapTreeSelectValue = (selectedValue: TreeSelectValue): string[] => {
    if (selectedValue === undefined || selectedValue === null) return [];
    return Array.isArray(selectedValue) ? selectedValue.map(asStringValue) : [asStringValue(selectedValue)];
};

const FormTreeSelector = memo((props: FormTreeSelectorProps) => {
    const {
        label,
        name,
        onChange,
        disabled,
        placeholder,
        isLoading,
        error,
        errorMessage,
        maxSelectionLength,
        minSelectionLength,
        tree,
        value,
        style,
        footer,
    } = props;

    const selectedKeys = useMemo(() => getSelectedKeys(value), [value]);
    const selectedValues = useMemo(() => getSelectedValuesByKeys(tree, selectedKeys), [tree, selectedKeys]);
    const isMultiple = maxSelectionLength > 1;
    const allowClear = !disabled && (minSelectionLength === 0 || isMultiple);
    const treeSelectValue = isMultiple ? selectedValues : selectedValues[0];

    return (
        <>
            <TreeSelectorDropdownStyles />
            <StyledWidgetSelector $error={!isLoading && error} style={style}>
                <Row>
                    <Cell className='widget-type-block'>
                        <span>{label}</span>
                    </Cell>
                </Row>

                <Row>
                    <StyledSelectCell data-entity-id={name} data-entity-type={ENTITY_TYPE.FORM_FIELD}>
                        <TreeSelect
                            data-testid={name}
                            allowClear={allowClear}
                            disabled={disabled}
                            placeholder={placeholder}
                            showSearch={false}
                            labelInValue
                            treeCheckable={isMultiple}
                            treeCheckStrictly={isMultiple}
                            treeData={tree}
                            treeDefaultExpandAll
                            listHeight={400}
                            value={treeSelectValue}
                            popupClassName='form-tree-selector-dropdown'
                            popupMatchSelectWidth={false}
                            onChange={(selectedValue: TreeSelectProps['value']) => {
                                onChange(mapTreeSelectValue(selectedValue as TreeSelectValue));
                            }}
                        />

                        {!isLoading && error && <div className='error'>{errorMessage}</div>}
                    </StyledSelectCell>
                </Row>

                {footer && <FooterInputText text={footer} />}
            </StyledWidgetSelector>
        </>
    );
});

export default FormTreeSelector;
